import { X } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect } from "react";

interface BillModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Extra classes for the panel (e.g. to widen or cap height). */
  className?: string;
}

/**
 * Responsive modal used across the bills flow.
 * - Mobile: bottom sheet that slides up.
 * - Desktop: centered dialog.
 * Matches Visaro's existing framer-motion language (see Confirm / BillPurchaseSuccess).
 */
const BillModal = ({
  open,
  onClose,
  title,
  description,
  children,
  className = "",
}: BillModalProps) => {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          <motion.div
            initial={{ y: "100%", opacity: 0.6 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0.6 }}
            transition={{ type: "tween", ease: "easeOut", duration: 0.25 }}
            className={`relative z-10 w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-xl flex flex-col max-h-[85vh] ${className}`}
          >
            <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 shrink-0">
              <div>
                <h3 className="font-bold text-lg text-gray-900">{title}</h3>
                {description && (
                  <p className="text-sm text-gray-500 mt-0.5">{description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition shrink-0"
                aria-label="Close"
              >
                <X weight="bold" size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BillModal;
